// src/pages/MapDoctors.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { loadGoogleMaps } from "../mapLoader"; // your existing loader

const MD = {
  primary: "#00A8FF",
  text: "#111827",
  subtle: "#64748B",
  border: "#E5E7EB",
  card: "#FFFFFF",
  bg: "#FFFFFF",
};

export default function MapDoctors() {
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [isMapReady, setIsMapReady] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [center, setCenter] = useState({ lat: 41.3275, lng: 19.8187 }); // Tirana default
  const [radiusKm, setRadiusKm] = useState(25);
  const [loading, setLoading] = useState(true);
  const [locErr, setLocErr] = useState("");
  const mapRef = useRef(null);
  const gmap = useRef(null);
  const markers = useRef([]);
  const circle = useRef(null);
  const navigate = useNavigate();

  // load maps
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!GOOGLE_MAPS_API_KEY) return;
      const ok = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);
      if (alive) setIsMapReady(!!ok);
    })();
    return () => (alive = false);
  }, [GOOGLE_MAPS_API_KEY]);

  // load doctors
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, "doctors"));
        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const data = d.data();
            const coords = data?.location?.coordinates;
            if (!coords?.lat || !coords?.lng) return null;

            let photoUrl = data.photoURL || data.photoUrl || data.profilePicture;
            if (!photoUrl) {
              try {
                const folderRef = ref(storage, `doctor_photos/${d.id}`);
                const files = await listAll(folderRef);
                if (files.items.length > 0) {
                  photoUrl = await getDownloadURL(files.items[0]);
                }
              } catch {
                /* ignore */
              }
            }
            return {
              id: d.id,
              name: data.name || "Doctor",
              specialty: data.specialty || "General",
              address: data?.location?.address || "",
              position: { lat: coords.lat, lng: coords.lng },
              photoUrl: photoUrl || "",
            };
          })
        );
        const clean = rows.filter(Boolean);
        if (alive) setDoctors(clean);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, []);

  const filtered = useMemo(() => {
    if (!center || !radiusKm) return doctors;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    return doctors.filter((d) => {
      const dLat = toRad(d.position.lat - center.lat);
      const dLon = toRad(d.position.lng - center.lng);
      const lat1 = toRad(center.lat);
      const lat2 = toRad(d.position.lat);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const dist = R * c;
      return dist <= radiusKm;
    });
  }, [doctors, center, radiusKm]);

  // init map when ready
  useEffect(() => {
    if (!isMapReady || !mapRef.current || gmap.current) return;
    gmap.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
    // draw initial circle
    circle.current = new window.google.maps.Circle({
      strokeColor: MD.primary,
      strokeOpacity: 0.7,
      strokeWeight: 1,
      fillColor: MD.primary,
      fillOpacity: 0.08,
      map: gmap.current,
      center,
      radius: radiusKm * 1000,
    });
  }, [isMapReady]); // eslint-disable-line

  // update circle + markers when center/radius or doctor list changes
  useEffect(() => {
    if (!gmap.current) return;

    // update circle
    if (circle.current) {
      circle.current.setCenter(center);
      circle.current.setRadius(radiusKm * 1000);
    }

    // center map (soft)
    gmap.current.setCenter(center);

    // clear old markers
    markers.current.forEach((m) => m.setMap(null));
    markers.current = [];

    const infowin = new window.google.maps.InfoWindow();

    filtered.forEach((d) => {
      const m = new window.google.maps.Marker({
        position: d.position,
        map: gmap.current,
        title: d.name,
      });
      m.addListener("click", () => {
        infowin.setContent(`
          <div style="min-width:180px">
            <div style="font-weight:700;color:#111827;margin-bottom:4px">${d.name}</div>
            <div style="font-size:12px;color:#64748B">${d.specialty}</div>
            <div style="font-size:12px;color:#64748B;margin-top:4px">${d.address || ""}</div>
            <div style="margin-top:8px">
              <a id="md-view-${d.id}" style="font-size:12px;color:${MD.primary};font-weight:700;cursor:pointer">View profile</a>
            </div>
          </div>
        `);
        infowin.open(gmap.current, m);
        window.setTimeout(() => {
          const el = document.getElementById(`md-view-${d.id}`);
          if (el) el.onclick = () => navigate(`/doctor/${d.id}`);
        }, 0);
      });
      markers.current.push(m);
    });
  }, [filtered, center, radiusKm, navigate]);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocErr("Geolocation not supported by this browser.");
      return;
    }
    setLocErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        if (gmap.current) gmap.current.setCenter(c);
      },
      () => setLocErr("Could not get your location."),
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 300000 }
    );
  }, []);

  const searchThisArea = useCallback(() => {
    if (!gmap.current) return;
    const c = gmap.current.getCenter();
    setCenter({ lat: c.lat(), lng: c.lng() });
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: MD.bg }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${MD.border}`,
        }}
      >
        <div
          style={{
            maxWidth: 1040,
            margin: "0 auto",
            padding: "10px 12px",
            display: "flex",
            gap: 8,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: MD.text, fontWeight: 800 }}>
            Doctors Map
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select
              value={radiusKm}
              onChange={(e) => setRadiusKm(parseInt(e.target.value || "25", 10))}
              style={{
                border: `1px solid ${MD.border}`,
                borderRadius: 10,
                padding: "8px 10px",
                background: "#fff",
                fontSize: 14,
              }}
            >
              {[5, 10, 15, 25, 50, 100].map((r) => (
                <option key={r} value={r}>
                  Radius: {r} km
                </option>
              ))}
            </select>
            <button
              onClick={useMyLocation}
              style={{
                border: `1px solid ${MD.border}`,
                background: "#F8FAFC",
                borderRadius: 10,
                padding: "8px 12px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Near me
            </button>
            <button
              onClick={searchThisArea}
              style={{
                border: `1px solid ${MD.border}`,
                background: MD.card,
                borderRadius: 10,
                padding: "8px 12px",
                fontWeight: 700,
                cursor: "pointer",
                color: MD.primary,
              }}
            >
              Search this area
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1040, margin: "0 auto" }}>
        {locErr && (
          <div style={{ padding: 12, color: "#B91C1C" }}>{locErr}</div>
        )}
        <div
          ref={mapRef}
          style={{
            height: "calc(100vh - 112px)",
            width: "100%",
            border: `1px solid ${MD.border}`,
            borderRadius: 16,
            overflow: "hidden",
          }}
        />
        {loading && (
          <div style={{ marginTop: 10, color: MD.subtle }}>Loading doctors…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ marginTop: 10, color: MD.subtle }}>No doctors in this radius.</div>
        )}
      </main>
    </div>
  );
}
